import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import { docsApi } from '@/shared/docsApi';

const STATUS_STYLES: Record<string, string> = {
  completed:       'bg-emerald-100 text-emerald-800 border border-emerald-200',
  awaiting_review: 'bg-amber-100 text-amber-800 border border-amber-200',
  failed:          'bg-rose-100 text-rose-800 border border-rose-200',
  processing:      'bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200',
  queued:          'bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200',
};

const DECISION_STYLES: Record<string, string> = {
  pass:               'bg-emerald-100 text-emerald-800 border border-emerald-200',
  pass_with_warnings: 'bg-amber-100 text-amber-800 border border-amber-200',
  hard_block:         'bg-rose-100 text-rose-800 border border-rose-200',
};

export function DocValidationJobListPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['doc-jobs'],
    queryFn: () => docsApi.list(),
    refetchInterval: 15_000,
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-axis-ink inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-axis-maroon" />
            Document Validation
          </h1>
          <p className="text-sm text-axis-ash mt-1">
            KYC, nominee, and fraud-signal checks for account onboarding and lifecycle changes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Link to="/docs/new" className="btn-primary">
            <Plus className="w-4 h-4" />
            New validation
          </Link>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Job ID</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Decision</th>
              <th className="text-left px-4 py-3">Account holder</th>
              <th className="text-right px-4 py-3">Documents</th>
              <th className="text-right px-4 py-3">Findings</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">
                No validations yet. <Link to="/docs/new" className="text-axis-maroon underline">Start one</Link>.
              </td></tr>
            ) : data.rows.map((job) => (
              <tr key={job.id} className="hover:bg-axis-cloud-50">
                <td className="px-4 py-3 font-mono text-xs">{job.id.slice(0, 12)}…</td>
                <td className="px-4 py-3">
                  <span className={`pill ${STATUS_STYLES[job.status] ?? STATUS_STYLES.queued}`}>{job.status}</span>
                </td>
                <td className="px-4 py-3">
                  {job.report?.decision
                    ? <span className={`pill ${DECISION_STYLES[job.report.decision]}`}>{job.report.decision}</span>
                    : <span className="text-axis-ash">—</span>}
                </td>
                <td className="px-4 py-3">{job.account_holder_name ?? <span className="text-axis-ash">—</span>}</td>
                <td className="px-4 py-3 text-right">{job.report?.totals.documents_evaluated ?? '—'}</td>
                <td className="px-4 py-3 text-right">
                  {job.report ? (job.report.totals.hard_blocks + job.report.totals.warnings + job.report.totals.infos) : '—'}
                </td>
                <td className="px-4 py-3 text-xs text-axis-ash">
                  {format(new Date(job.created_at), 'd MMM yyyy, HH:mm:ss.SSS')}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/docs/${job.id}`} className="text-axis-maroon hover:underline text-sm">View →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
